import { FaireMarketIcon } from "@faire/deprecated-retailer-visitor-shared/features/FaireMarkets/FaireMarketIcon";
import { useMarketStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { useMarketCurrentEvent } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/useMarketCurrentEvent";
import { Core } from "@faire/design-tokens";
import { useClock } from "@faire/web/common/useClock";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Column, Row, VerticalLine } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { eventsThemeColors } from "@faire/web/slate/Theme/eventsThemeColors";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { differenceInDays } from "date-fns/differenceInDays";
import React from "react";
import styled from "styled-components";

interface IProps {
  faireMarketSectionId: string;
}

export const BrandsLandingPageFaireMarketBanner: React.FC<IProps> = ({
  faireMarketSectionId,
}) => {
  return (
    <ResponsiveContentV2
      mobile={
        <Container>
          <Column>
            <Row align="center">
              <SyledIcon />
              <Spacer width="2x" />
              <Title />
            </Row>
            <Spacer height="3x" />
            <CtaSection faireMarketSectionId={faireMarketSectionId} />
          </Column>
        </Container>
      }
      tabletAndAbove={
        <Container>
          <Row align="center">
            <SyledIcon />
            <Spacer width="2x" />
            <Title />
          </Row>
          <Spacer width="10x" />
          <CtaSection faireMarketSectionId={faireMarketSectionId} />
        </Container>
      }
    />
  );
};

const CtaSection = ({
  faireMarketSectionId,
}: {
  faireMarketSectionId: string;
}) => {
  const { startAt } = useMarketStore();
  const clock = useClock();
  const difference = differenceInDays(startAt, clock.date);

  return (
    <CtaSectionWrapper align="center">
      {difference > 0 ? (
        <>
          <Typography variant="paragraphSansMedium">
            <StrictLocalMsg
              id="vjDghF"
              defaultMessage="{differenceNumber, plural, one{# day until the market} other{# days until the market}}"
              description={{
                text: "Days remaining until the end of the promotional event",
              }}
              values={{ differenceNumber: difference }}
            />
          </Typography>
          <Spacer width="2x" />
          <VerticalLine $height="16px" $color={Core.border.muted} />
          <Spacer width="2x" />
        </>
      ) : null}
      <Button variant="tertiary" linkTo={`#${faireMarketSectionId}`}>
        <StrictLocalMsg id="7DIW6+" defaultMessage="Learn More" />
      </Button>
    </CtaSectionWrapper>
  );
};

const Title = () => {
  const { marketName } = useMarketCurrentEvent();

  return (
    <Typography variant="paragraphSansMedium">
      <StrictLocalMsg
        id="l8178n"
        defaultMessage="Join Faire in time to sell at {marketEventName}"
        values={{
          marketEventName: marketName,
        }}
      />
    </Typography>
  );
};

const Container = styled(Row).attrs({
  align: "center",
  justify: "space-between",
})`
  background-color: ${eventsThemeColors.faireMarket.lightBackground};
  padding: ${getSpacing("3x", "5x")};
  height: fit-content;
  width: 100%;

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x", "3x", "2x")};
  }
`;

const CtaSectionWrapper = styled(Row)`
  min-width: 278px;
`;

const SyledIcon = styled(FaireMarketIcon).attrs({
  size: "32px",
})`
  ${responsiveMediaQueries.mobile} {
    align-self: start;
  }
`;
